import React, { useEffect, useRef, useState } from "react";
import { TrendingUp } from "lucide-react";
import {
  NET_MARGIN_PCT,
  REVENUE_VELOCITY_USD,
  ROI_MULTIPLIER,
  fmtUsdK,
} from "../landing/aigoMetrics";

/**
 * GrowthHud — the persistent top-right overlay for the 45-second demo.
 *
 * Shows the three-metric growth story ticking together across the whole
 * video: Revenue climbs → Net Margin expands → ROI locks in.
 *
 *   Revenue:  $48.7k → $88.6k (Revenue Velocity, real)
 *   Margin:   48.0% → 53.7%   (Net Margin, real target; +5.7pt expansion)
 *   ROI:      —     → 7.32x   (appears at 40% through, real)
 *
 * All easings are RAF-based and keyed to `loopId` so restarting the loop
 * resets the counters cleanly. Delta badges + pulse-glow fire when each
 * metric crosses its target.
 */
const REV_START = 48700;   // real "before" revenue from pnl.png ($48.7k)
const REV_END   = REVENUE_VELOCITY_USD;
const MARGIN_START = 48.0;  // user-approved "before" baseline
const MARGIN_END   = NET_MARGIN_PCT;
const ROI_END      = ROI_MULTIPLIER;

const ROI_APPEAR_AT = 0.40; // ROI card reveals at 40% of total duration

export const GrowthHud = ({ loopId = 0, totalMs, sceneIndex, sceneCount }) => {
  const [rev, setRev] = useState(REV_START);
  const [margin, setMargin] = useState(MARGIN_START);
  const [roiVisible, setRoiVisible] = useState(false);
  const [hitTarget, setHitTarget] = useState({ rev: false, margin: false });
  const raf = useRef(null);

  useEffect(() => {
    const start = performance.now();
    setRev(REV_START);
    setMargin(MARGIN_START);
    setRoiVisible(false);
    setHitTarget({ rev: false, margin: false });

    const step = (now) => {
      const p = Math.min(1, (now - start) / totalMs);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic

      setRev(Math.round(REV_START + (REV_END - REV_START) * eased));
      setMargin(+(MARGIN_START + (MARGIN_END - MARGIN_START) * eased).toFixed(1));
      if (p >= ROI_APPEAR_AT) setRoiVisible(true);
      if (p >= 0.98) setHitTarget({ rev: true, margin: true });

      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [loopId, totalMs]);

  const marginDelta = +(margin - MARGIN_START).toFixed(1);
  const revDelta = rev - REV_START;

  return (
    <div
      data-testid="pd-growth-hud"
      className="absolute top-2.5 right-2.5 sm:top-3.5 sm:right-3.5 z-30 select-none"
    >
      <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 rounded-xl bg-white/95 backdrop-blur-md border border-slate-200 shadow-md">
        {/* Revenue */}
        <Metric
          label="Revenue"
          value={fmtUsdK(rev)}
          delta={revDelta > 500 ? `+${fmtUsdK(revDelta)}` : null}
          glow={hitTarget.rev}
          testid="hud-revenue"
        />
        <span className="w-px h-8 bg-slate-200" />

        {/* Margin */}
        <Metric
          label="Margin"
          value={`${margin.toFixed(1)}%`}
          delta={marginDelta >= 1 ? `+${marginDelta.toFixed(1)}pt` : null}
          glow={hitTarget.margin}
          testid="hud-margin"
        />

        {/* ROI (appears later) */}
        <span
          className="hidden sm:block w-px h-8 bg-slate-200 transition-opacity"
          style={{ opacity: roiVisible ? 1 : 0 }}
        />
        <div
          className="hidden sm:block transition-all"
          style={{
            opacity: roiVisible ? 1 : 0,
            transform: roiVisible ? "scale(1) translateY(0)" : "scale(0.7) translateY(-4px)",
          }}
        >
          <Metric label="ROI" value={roiVisible ? `${ROI_END.toFixed(2)}x` : "—"} glow={roiVisible} testid="hud-roi" />
        </div>

        {/* Scene counter */}
        <span className="text-slate-400 text-[8px] sm:text-[9px] tracking-[0.16em] font-medium tabular-nums pl-1">
          {String(sceneIndex + 1).padStart(2, "0")}/{String(sceneCount).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
};

const Metric = ({ label, value, delta, glow, testid }) => (
  <div className="flex flex-col leading-none" data-testid={testid}>
    <div className="flex items-center gap-1">
      <span className="text-[8px] sm:text-[9px] tracking-[0.18em] font-bold text-slate-500 uppercase">
        {label}
      </span>
      {delta && (
        <span
          className="text-[8px] sm:text-[9px] font-bold tabular-nums text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-[1px]"
          style={{ animation: "cap-in 400ms ease-out both" }}
        >
          <TrendingUp className="inline w-2 h-2 mr-0.5 -mt-0.5" />
          {delta}
        </span>
      )}
    </div>
    <span
      className="text-sm sm:text-base font-extrabold tabular-nums transition-colors"
      style={{
        color: glow ? "#059669" : "#0f172a",
        textShadow: glow ? "0 0 12px rgba(16,185,129,0.35)" : "none",
      }}
    >
      {value}
    </span>
  </div>
);

export default GrowthHud;
